/*
 * Todolist du CRM Sun Is Up : calcul partagé entre l'onglet Communications (navigateur)
 * et le récap mensuel envoyé à info@sunisup.be (fonction crm-monthly-digest).
 * Module sans dépendance : uniquement des données en entrée, une liste en sortie.
 *
 * - Leads à traiter : contacts du pipeline marqués « en attente de Sun Is Up ».
 * - Onboarding : membres actifs dont le message de bienvenue n'est pas envoyé.
 * - Comptes à activer : membres actifs sans accès à la plateforme (même règle que le tableau de bord admin :
 *   aucune fiche participant avec un e-mail de connexion). Après une invitation, la ligne attend 30 jours avant relance.
 * - Rapports semestriels : une tâche par membre (un groupe = une tâche), tous les 6 mois après le début du partage.
 * - Remboursements : producteurs et prosumers, une ligne par société (n° BCE) dans un groupe, par EAN sinon.
 *   Mensuel : chaque mois dès réception des données. Trimestriel / semestriel : périodes fixes du calendrier.
 * - Facturation : consommateurs, toujours mensuelle, dès réception des données.
 * - Prosumers (toute société / EAN qui compte un prosumer) : une tâche par mois, facture si le solde est en faveur de la
 *   Communauté, décompte (remboursement) sinon ; le solde est calculé dans le CRM (même calcul que le PDF).
 *   « Décaler au mois suivant » (2 fois au plus) : la tâche revient le mois suivant avec 2 mois de données (3 au plus).
 */

export type ReimbursementPeriod = 'monthly' | 'quarterly' | 'semiannual';
/** Canal de facturation des consommateurs : Accountable (sociétés ; valeur stockée « peppol »), e-mail, domiciliation SEPA. */
export type BillingChannel = 'peppol' | 'email' | 'direct_debit';
export const BILLING_CHANNELS: BillingChannel[] = ['peppol', 'email', 'direct_debit'];
export const BILLING_CHANNEL_LABELS: Record<BillingChannel, string> = { peppol: 'Accountable', email: 'E-mail', direct_debit: 'Domiciliation' };
export type TodoKind = 'lead' | 'onboarding' | 'invite' | 'report' | 'reimbursement' | 'invoice';

export interface TodoParticipant {
  id: string;
  name: string | null;
  type: string | null;
  groupe: string | null;
  company_number: string | null;
  company_name: string | null;
  entry_date: string | null;
  sharing_operation_id: string | null;
  reimbursement_period: string | null;
  /** Accès plateforme créé (participants.email renseigné). */
  has_account?: boolean;
  /** Mois pour lesquels des données sont disponibles (« AAAA-MM »). */
  months: string[];
}

export interface TodoContact {
  id: string;
  name: string;
  email: string | null;
  stage: string;
  groupe: string | null;
  merged_into_id: string | null;
  archived_at: string | null;
  sharing_operation_id: string | null;
  sharing_start_date: string | null;
  onboarding_sent_at: string | null;
  participant_ids: string[];
  /** Pipeline : en attente du client ou de Sun Is Up. */
  waiting_on?: string | null;
  waiting_note?: string | null;
  waiting_since?: string | null;
  invitation_sent_at?: string | null;
  /* Facturation : canal choisi dans la fiche et éléments du choix automatique. */
  role?: string | null;
  entity_type?: string | null;
  billing_channel?: string | null;
  mandate_status?: string | null;
}

export interface TodoDone {
  kind: string;
  ref: string;
  period: string;
}

export interface TodoOperationSettings {
  /** Suivie dans la todolist et le récap. */
  tracked: boolean;
  /** Consommateurs facturés jusqu'à ce mois inclus (« AAAA-MM »). */
  invoicedThrough: string | null;
  /** Producteurs remboursés pour les périodes qui se terminent au plus tard ce mois-ci. */
  reimbursedThrough: string | null;
}

export interface TodoSettings {
  operations: Record<string, TodoOperationSettings>;
  /** Rapports semestriels suivis à partir de ce mois (« AAAA-MM »). */
  reportsFrom: string;
}

export interface TodoItem {
  kind: TodoKind;
  /** Identifiant stable de la ligne (pour la cocher). */
  ref: string;
  /** Période concernée (« AAAA-MM », « AAAA-T3 », « AAAA-S2 »). */
  period: string;
  periodLabel: string;
  title: string;
  subtitle: string;
  operationId: string | null;
  /** Fiche CRM à ouvrir, si elle existe. */
  contactId: string | null;
  /** Noms des EAN concernés (remboursement, facturation). */
  eans: string[];
  /** Fiches participants concernées (pour pré-cocher l'outil de factures). */
  participantIds: string[];
  /** Mois dont les données manquent encore (vide = prêt). */
  missingMonth: string | null;
  /** Rapport dont l'échéance est un mois antérieur au mois en cours. */
  overdue: boolean;
  /** Premier et dernier mois de la période (« AAAA-MM ») : facturation et remboursements uniquement. */
  periodStart: string;
  periodEnd: string;
  /** N° BCE de la société (facturation, remboursements) ; null pour un particulier. */
  companyNumber: string | null;
  /** Facturation : canal du membre (choisi dans la fiche, sinon automatique). */
  billingChannel?: BillingChannel;
  /** Prosumer : facture ou décompte selon le solde (kind fixé dans le CRM après calcul). */
  prosumer?: boolean;
  /** Prosumer : nombre de reports au mois suivant (0 à 2). */
  deferrals?: number;
  /** Prosumer décalé : liste dans laquelle il était au moment du report. */
  deferredKind?: 'invoice' | 'reimbursement';
}

/** Report d'une tâche prosumer au mois suivant (crm_todo_done : kind « defer_invoice » ou « defer_reimbursement », period = mois reporté). */
export const DEFER_KINDS = ['defer_invoice', 'defer_reimbursement'] as const;
export const MAX_DEFERRALS = 2;

export interface TodoList {
  month: string;
  leads: TodoItem[];
  onboarding: TodoItem[];
  /** Membres actifs sans compte : à inviter (ready) ou invités depuis moins de 30 jours (waiting). */
  invites: { ready: TodoItem[]; waiting: TodoItem[] };
  reports: TodoItem[];
  reimbursements: { ready: TodoItem[]; waiting: TodoItem[] };
  invoices: { ready: TodoItem[]; waiting: TodoItem[] };
  /** Prosumers : prêts (à classer selon le solde), en attente des données, décalés au mois suivant. */
  prosumers: { ready: TodoItem[]; waiting: TodoItem[]; deferred: TodoItem[] };
}

const STAGE_LABELS: Record<string, string> = {
  lead: 'Lead', contract_sent: 'Contrat envoyé', contract_signed: 'Contrat signé', sibelga: 'Encodé Sibelga', active: 'Membre actif',
};

const MONTHS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const QUARTER_FR = ['1er', '2e', '3e', '4e'];

/* ── Mois « AAAA-MM » ── */

export const monthOf = (iso: string | null | undefined): string | null => (iso && /^\d{4}-\d{2}/.test(iso) ? iso.slice(0, 7) : null);

export function addMonths(month: string, n: number): string {
  const [y, m] = month.split('-').map(Number);
  const total = y * 12 + (m - 1) + n;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
}

export function monthRange(from: string, to: string): string[] {
  const out: string[] = [];
  for (let m = from; m <= to; m = addMonths(m, 1)) out.push(m);
  return out;
}

export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return `${MONTHS_FR[m - 1]} ${y}`;
}

/** Mois en cours à Bruxelles. */
export function currentMonth(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('fr-BE', { timeZone: 'Europe/Brussels', year: 'numeric', month: '2-digit' }).formatToParts(now);
  const y = parts.find(p => p.type === 'year')?.value ?? String(now.getFullYear());
  const m = parts.find(p => p.type === 'month')?.value ?? String(now.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

/* ── Périodes de remboursement ── */

export const PERIOD_LABELS: Record<ReimbursementPeriod, string> = {
  monthly: 'Mensuelle',
  quarterly: 'Trimestrielle',
  semiannual: 'Semestrielle',
};

export function normalizePeriod(value: string | null | undefined): ReimbursementPeriod {
  return value === 'monthly' || value === 'semiannual' ? value : 'quarterly';
}

interface Period { key: string; label: string; start: string; end: string }

/** Période du calendrier qui contient un mois (mois, trimestre jan–mars…, semestre jan–juin / juil–déc). */
export function periodContaining(month: string, kind: ReimbursementPeriod): Period {
  const [y, m] = month.split('-').map(Number);
  if (kind === 'monthly') return { key: month, label: monthLabel(month), start: month, end: month };
  if (kind === 'quarterly') {
    const q = Math.floor((m - 1) / 3);
    const start = `${y}-${String(q * 3 + 1).padStart(2, '0')}`;
    return { key: `${y}-T${q + 1}`, label: `${QUARTER_FR[q]} trimestre ${y}`, start, end: addMonths(start, 2) };
  }
  const s = m <= 6 ? 0 : 1;
  const start = `${y}-${s === 0 ? '01' : '07'}`;
  return { key: `${y}-S${s + 1}`, label: `${s === 0 ? '1er' : '2e'} semestre ${y}`, start, end: addMonths(start, 5) };
}

/* ── Regroupement « une ligne par société » ── */

const lower = (v: string | null | undefined) => (v ?? '').trim().toLowerCase();
const digits = (v: string | null | undefined) => (v ?? '').replace(/\D/g, '');

interface Entity {
  ref: string; title: string; subtitle: string; operationId: string | null; participants: TodoParticipant[];
  /** N° BCE tel qu'enregistré sur la première fiche qui en a un ; null pour un particulier. */
  companyNumber: string | null;
}

function entitiesOf(participants: TodoParticipant[]): Entity[] {
  const map = new Map<string, Entity>();
  for (const p of participants) {
    const groupe = p.groupe?.trim();
    const ref = groupe ? `g:${lower(groupe)}|${digits(p.company_number)}` : `p:${p.id}`;
    let e = map.get(ref);
    if (!e) {
      e = { ref, title: '', subtitle: '', operationId: p.sharing_operation_id, participants: [], companyNumber: null };
      map.set(ref, e);
    }
    e.participants.push(p);
  }
  for (const e of map.values()) {
    const first = e.participants[0];
    e.companyNumber = e.participants.find(p => digits(p.company_number))?.company_number?.trim() ?? null;
    const groupe = first.groupe?.trim();
    if (groupe) {
      const company = e.participants.find(p => p.company_name?.trim())?.company_name?.trim();
      e.title = company || (digits(first.company_number) ? `Société ${first.company_number}` : groupe);
      const groupLabel = /^groupe\b/i.test(groupe) ? groupe : `Groupe ${groupe}`;
      e.subtitle = `${groupLabel}${company && digits(first.company_number) ? ` · ${first.company_number}` : ''}`;
    } else {
      e.title = first.name?.trim() || 'Sans nom';
      e.subtitle = first.company_name?.trim() && first.company_name.trim() !== e.title ? first.company_name.trim() : '';
    }
  }
  return Array.from(map.values());
}

/** Premier mois sans données pour la période (en tenant compte de la date d'entrée de chaque EAN), ou null si tout est là. */
/** Mois couverts par une clé de période (« AAAA-MM », « AAAA-T2 », « AAAA-S1 », « AAAA-MM_AAAA-MM »). */
export function monthsOfPeriodKey(key: string): string[] {
  let m = /^(\d{4}-\d{2})_(\d{4}-\d{2})$/.exec(key);
  if (m) return monthRange(m[1], m[2]);
  if (/^\d{4}-\d{2}$/.test(key)) return [key];
  m = /^(\d{4})-([TS])(\d)$/.exec(key);
  if (m) {
    const y = Number(m[1]); const n = Number(m[3]);
    const start = m[2] === 'T' ? `${y}-${String((n - 1) * 3 + 1).padStart(2, '0')}` : `${y}-${n === 1 ? '01' : '07'}`;
    return monthRange(start, addMonths(start, m[2] === 'T' ? 2 : 5));
  }
  return [];
}

/** Libellé d'une période de prosumer : « avril 2026 » ou « avril – mai 2026 (2 mois) ». */
export function windowLabel(start: string, end: string): string {
  if (start === end) return monthLabel(start);
  const n = monthRange(start, end).length;
  const [ys] = start.split('-'); const [ye] = end.split('-');
  const from = ys === ye ? monthLabel(start).replace(` ${ys}`, '') : monthLabel(start);
  return `${from} – ${monthLabel(end)} (${n} mois)`;
}

function missingMonthFor(e: Entity, start: string, end: string): string | null {
  let missing: string | null = null;
  for (const p of e.participants) {
    const entry = monthOf(p.entry_date) ?? start;
    if (entry > end) continue;
    const months = new Set(p.months);
    for (const m of monthRange(entry > start ? entry : start, end)) {
      if (!months.has(m)) { if (!missing || m < missing) missing = m; break; }
    }
  }
  return missing;
}

/* ── Calcul ── */

const NO_PERIOD = { periodStart: '', periodEnd: '', companyNumber: null } as const;

/**
 * Canal de facturation : celui de la fiche, sinon particulier consommateur avec mandat actif → domiciliation,
 * société (n° BCE) → Accountable, autres → e-mail.
 */
export function resolveBillingChannel(
  contact: Pick<TodoContact, 'billing_channel' | 'role' | 'entity_type' | 'mandate_status'> | null | undefined,
  item: { companyNumber: string | null },
): BillingChannel {
  const explicit = contact?.billing_channel;
  if (explicit === 'peppol' || explicit === 'email' || explicit === 'direct_debit') return explicit;
  if (contact && contact.role === 'consumer' && contact.entity_type === 'physique' && contact.mandate_status === 'active') return 'direct_debit';
  if (digits(item.companyNumber)) return 'peppol';
  return 'email';
}

export interface TodoInput {
  participants: TodoParticipant[];
  contacts: TodoContact[];
  done: TodoDone[];
  settings: TodoSettings;
  month?: string;
  /** Horodatage de référence (tests) ; par défaut maintenant. */
  now?: number;
}

export function computeTodo({ participants, contacts, done, settings, month = currentMonth(), now = Date.now() }: TodoInput): TodoList {
  const isDone = new Set(done.map(d => `${d.kind}|${d.ref}|${d.period}`));
  /** Reports au mois suivant : « ref|mois » -> liste au moment du report. */
  const deferredAt = new Map<string, 'invoice' | 'reimbursement'>();
  /** Mois déjà facturés ou remboursés par entité (toutes périodes confondues). */
  const coveredMonths = new Map<string, Set<string>>();
  for (const d of done) {
    if (d.kind === 'defer_invoice' || d.kind === 'defer_reimbursement') {
      deferredAt.set(`${d.ref}|${d.period}`, d.kind === 'defer_invoice' ? 'invoice' : 'reimbursement');
    } else if (d.kind === 'invoice' || d.kind === 'reimbursement') {
      const set = coveredMonths.get(d.ref) ?? new Set<string>();
      for (const m of monthsOfPeriodKey(d.period)) set.add(m);
      coveredMonths.set(d.ref, set);
    }
  }
  const tracked = (opId: string | null) => !opId || settings.operations[opId]?.tracked !== false;
  const opSettings = (opId: string | null): TodoOperationSettings =>
    (opId && settings.operations[opId]) || { tracked: true, invoicedThrough: null, reimbursedThrough: null };

  const liveParticipants = participants.filter(p => tracked(p.sharing_operation_id));
  const cards = contacts.filter(c => c.stage === 'active' && !c.archived_at && !c.merged_into_id && tracked(c.sharing_operation_id));
  const cardFor = (participantId: string, groupe: string | null): string | null => {
    if (groupe) return cards.find(c => lower(c.groupe) === lower(groupe))?.id ?? null;
    return cards.find(c => c.participant_ids.includes(participantId))?.id ?? null;
  };

  /* Leads à traiter : en attente de Sun Is Up (les contacts sans opération sont toujours inclus) */
  const leads: TodoItem[] = contacts
    .filter(c => c.stage !== 'active' && !c.archived_at && c.waiting_on === 'sunisup' && tracked(c.sharing_operation_id))
    .map(c => ({
      kind: 'lead', ref: c.id, period: (c.waiting_since ?? '').slice(0, 10), periodLabel: STAGE_LABELS[c.stage] ?? c.stage, title: c.name,
      subtitle: [c.waiting_note?.trim() || 'À traiter', c.waiting_since ? `depuis le ${formatDate(c.waiting_since)}` : ''].filter(Boolean).join(' · '),
      operationId: c.sharing_operation_id, contactId: c.id, eans: [], participantIds: [], missingMonth: null, overdue: false,
      ...NO_PERIOD,
    }));

  /* Onboarding */
  const onboarding: TodoItem[] = cards
    .filter(c => !c.onboarding_sent_at)
    .map(c => ({
      kind: 'onboarding', ref: c.id, period: '', periodLabel: '', title: c.name,
      subtitle: c.sharing_start_date ? `Partage depuis le ${formatDate(c.sharing_start_date)}` : 'Membre actif',
      operationId: c.sharing_operation_id, contactId: c.id, eans: [], participantIds: [], missingMonth: null, overdue: false,
      ...NO_PERIOD,
    }));

  /* Comptes à activer : aucune fiche participant du membre n'a d'accès plateforme */
  const RELAUNCH_MS = 30 * 24 * 3600 * 1000;
  const invitesReady: TodoItem[] = [];
  const invitesWaiting: TodoItem[] = [];
  for (const c of cards) {
    const members = c.groupe
      ? participants.filter(p => lower(p.groupe) === lower(c.groupe))
      : participants.filter(p => c.participant_ids.includes(p.id));
    if (members.length === 0 || members.some(p => p.has_account)) continue;
    const invited = c.invitation_sent_at ? Date.parse(c.invitation_sent_at) : NaN;
    const item: TodoItem = {
      kind: 'invite', ref: c.id, period: '', periodLabel: Number.isNaN(invited) ? 'jamais invité' : `invité le ${formatDate(c.invitation_sent_at!)}`,
      title: c.name, subtitle: c.email ? c.email : 'Pas d’e-mail : à ajouter dans la fiche', operationId: c.sharing_operation_id,
      contactId: c.id, eans: [], participantIds: members.map(p => p.id), missingMonth: null, overdue: false,
      ...NO_PERIOD,
    };
    (!Number.isNaN(invited) && now - invited < RELAUNCH_MS ? invitesWaiting : invitesReady).push(item);
  }

  /* Rapports semestriels : début du partage + 6, 12, 18… mois */
  const reports: TodoItem[] = [];
  for (const c of cards) {
    const startDate = c.sharing_start_date
      ?? participants.filter(p => (c.groupe ? lower(p.groupe) === lower(c.groupe) : c.participant_ids.includes(p.id)))
        .map(p => p.entry_date).filter((d): d is string => !!d).sort()[0]
      ?? null;
    const start = monthOf(startDate);
    if (!start) continue;
    for (let k = 1; ; k++) {
      const due = addMonths(start, 6 * k);
      if (due > month) break;
      if (due < settings.reportsFrom) continue;
      if (isDone.has(`report|${c.id}|${due}`)) continue;
      reports.push({
        kind: 'report', ref: c.id, period: due, periodLabel: `${6 * k} mois de partage (${monthLabel(due)})`,
        title: c.name, subtitle: c.email ?? 'Pas d’e-mail', operationId: c.sharing_operation_id, contactId: c.id,
        eans: [], participantIds: [], missingMonth: null, overdue: due < month,
        ...NO_PERIOD,
      });
    }
  }

  /* Remboursements (producteurs, prosumers) et facturation (consommateurs, prosumers) */
  /* Sociétés / EAN qui comptent un prosumer : traités à part (une tâche par mois, liste selon le solde). */
  const prosumerEntities = entitiesOf(liveParticipants).filter(e => e.participants.some(p => p.type === 'prosumer'));
  const prosumerRefs = new Set(prosumerEntities.map(e => e.ref));

  const build = (kind: 'reimbursement' | 'invoice') => {
    const roles = kind === 'reimbursement' ? ['producer'] : ['consumer'];
    const ready: TodoItem[] = [];
    const waiting: TodoItem[] = [];
    for (const e of entitiesOf(liveParticipants.filter(p => roles.includes(p.type ?? 'consumer')))) {
      if (prosumerRefs.has(e.ref)) continue;
      const counts = new Map<ReimbursementPeriod, number>();
      for (const p of e.participants) {
        const k = kind === 'invoice' ? 'monthly' : normalizePeriod(p.reimbursement_period);
        counts.set(k, (counts.get(k) ?? 0) + 1);
      }
      const periodKind = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'quarterly';
      const ops = opSettings(e.operationId);
      const through = kind === 'invoice' ? ops.invoicedThrough : ops.reimbursedThrough;
      const entry = e.participants.map(p => monthOf(p.entry_date)).filter((m): m is string => !!m).sort()[0];
      if (!entry) continue;
      const first = through && addMonths(through, 1) > entry ? addMonths(through, 1) : entry;
      // Périodes terminées au plus tard ce mois-ci : la période qui se termine ce mois-ci attend ses données (ex. juin pour avril–juin).
      for (let period = periodContaining(first, periodKind); period.end <= month; period = periodContaining(addMonths(period.end, 1), periodKind)) {
        if (through && period.end <= through) continue;
        if (isDone.has(`${kind}|${e.ref}|${period.key}`)) continue;
        const missing = missingMonthFor(e, period.start, period.end);
        const item: TodoItem = {
          kind, ref: e.ref, period: period.key, periodLabel: period.label, title: e.title,
          subtitle: [e.subtitle, `${e.participants.length} EAN`, kind === 'reimbursement' ? PERIOD_LABELS[periodKind].toLowerCase() : '']
            .filter(Boolean).join(' · '),
          operationId: e.operationId, contactId: cardFor(e.participants[0].id, e.participants[0].groupe),
          eans: e.participants.map(p => p.name?.trim() || p.id), participantIds: e.participants.map(p => p.id),
          missingMonth: missing, overdue: false,
          periodStart: period.start, periodEnd: period.end, companyNumber: e.companyNumber,
        };
        if (kind === 'invoice') item.billingChannel = resolveBillingChannel(cards.find(c => c.id === item.contactId), item);
        (missing ? waiting : ready).push(item);
      }
    }
    const order = (a: TodoItem, b: TodoItem) => a.period.localeCompare(b.period) || a.title.localeCompare(b.title, 'fr');
    return { ready: ready.sort(order), waiting: waiting.sort(order) };
  };

  /*
   * Prosumers : un mois par tâche ; un mois reporté est regroupé avec le suivant (2 reports au plus = 3 mois).
   * Point de départ : mois d'entrée, après les mois déjà facturés ou remboursés (cochés, ou repères des paramètres).
   */
  const buildProsumers = () => {
    const ready: TodoItem[] = [];
    const waiting: TodoItem[] = [];
    const deferred: TodoItem[] = [];
    for (const e of prosumerEntities) {
      const ops = opSettings(e.operationId);
      const through = [ops.invoicedThrough, ops.reimbursedThrough].filter((v): v is string => !!v).sort().pop() ?? null;
      const entry = e.participants.map(p => monthOf(p.entry_date)).filter((m): m is string => !!m).sort()[0];
      if (!entry) continue;
      const covered = coveredMonths.get(e.ref) ?? new Set<string>();
      const isCovered = (m: string) => covered.has(m) || (!!through && m <= through);
      let m = through && addMonths(through, 1) > entry ? addMonths(through, 1) : entry;
      while (m <= month) {
        if (isCovered(m)) { m = addMonths(m, 1); continue; }
        let end = m;
        let deferrals = 0;
        let deferredKind: 'invoice' | 'reimbursement' | undefined;
        while (deferrals < MAX_DEFERRALS && deferredAt.has(`${e.ref}|${end}`) && !isCovered(addMonths(end, 1))) {
          deferredKind = deferredAt.get(`${e.ref}|${end}`);
          end = addMonths(end, 1);
          deferrals += 1;
        }
        const key = end === m ? m : `${m}_${end}`;
        const next = addMonths(end, 1);
        if (isDone.has(`invoice|${e.ref}|${key}`) || isDone.has(`reimbursement|${e.ref}|${key}`)) { m = next; continue; }
        const missing = missingMonthFor(e, m, end);
        const item: TodoItem = {
          kind: deferredKind ?? 'invoice', ref: e.ref, period: key, periodLabel: windowLabel(m, end), title: e.title,
          subtitle: [e.subtitle, `${e.participants.length} EAN`, 'prosumer'].filter(Boolean).join(' · '),
          operationId: e.operationId, contactId: cardFor(e.participants[0].id, e.participants[0].groupe),
          eans: e.participants.map(p => p.name?.trim() || p.id), participantIds: e.participants.map(p => p.id),
          missingMonth: missing, overdue: false,
          periodStart: m, periodEnd: end, companyNumber: e.companyNumber,
          prosumer: true, deferrals, ...(deferredKind ? { deferredKind } : {}),
        };
        item.billingChannel = resolveBillingChannel(cards.find(c => c.id === item.contactId), item);
        // Report en cours : le mois ajouté n'a pas encore ses données.
        (missing ? (deferrals > 0 && missing > m ? deferred : waiting) : ready).push(item);
        m = next;
      }
    }
    const order = (a: TodoItem, b: TodoItem) => a.periodStart.localeCompare(b.periodStart) || a.title.localeCompare(b.title, 'fr');
    return { ready: ready.sort(order), waiting: waiting.sort(order), deferred: deferred.sort(order) };
  };

  return {
    month,
    // Les plus anciens d'abord ; sans date à la fin.
    leads: leads.sort((a, b) => (a.period || '9999').localeCompare(b.period || '9999') || a.title.localeCompare(b.title, 'fr')),
    onboarding: onboarding.sort((a, b) => a.title.localeCompare(b.title, 'fr')),
    invites: {
      ready: invitesReady.sort((a, b) => a.title.localeCompare(b.title, 'fr')),
      waiting: invitesWaiting.sort((a, b) => a.title.localeCompare(b.title, 'fr')),
    },
    reports: reports.sort((a, b) => a.period.localeCompare(b.period) || a.title.localeCompare(b.title, 'fr')),
    reimbursements: build('reimbursement'),
    invoices: build('invoice'),
    prosumers: buildProsumers(),
  };
}

function formatDate(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

/** Nombre de tâches à faire (hors attente de données). */
export function todoCount(list: TodoList): number {
  return list.leads.length + list.onboarding.length + list.invites.ready.length + list.reports.length + list.reimbursements.ready.length + list.invoices.ready.length
    + list.prosumers.ready.length;
}

/* ── E-mail du récap ── */

const esc = (v: string) =>
  v.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** Récap HTML envoyé à info@sunisup.be. */
export function renderTodoEmail(list: TodoList, siteUrl: string): string {
  const section = (title: string, items: TodoItem[], empty: string, line: (i: TodoItem) => string) => `
    <h3 style="font-size:15px;margin:24px 0 8px;color:#1a1a1a;">${esc(title)} (${items.length})</h3>
    ${items.length === 0
      ? `<p style="margin:0;color:#888;">${esc(empty)}</p>`
      : `<ul style="margin:0;padding-left:18px;">${items.map(i => `<li style="margin:4px 0;">${line(i)}</li>`).join("")}</ul>`}`;

  const waitingSummary = (label: string, items: TodoItem[]) => {
    const byPeriod = new Map<string, { count: number; missing: string }>();
    for (const i of items) {
      const k = `${i.periodLabel}|${i.missingMonth}`;
      const cur = byPeriod.get(k) ?? { count: 0, missing: i.missingMonth ?? "" };
      cur.count += 1;
      byPeriod.set(k, cur);
    }
    return [...byPeriod.entries()].map(([k, v]) =>
      `<li style="margin:4px 0;">${esc(label)} · ${esc(k.split("|")[0])} : ${v.count}, en attente des données de ${esc(monthLabel(v.missing))}</li>`).join("");
  };
  const waiting = waitingSummary("Remboursement", list.reimbursements.waiting) + waitingSummary("Facturation", list.invoices.waiting)
    + waitingSummary("Prosumer", list.prosumers.waiting) + waitingSummary("Prosumer décalé", list.prosumers.deferred);

  return `
  <div style="font-family:Arial,sans-serif;font-size:14px;color:#333;line-height:1.6;max-width:640px;">
    <h2 style="font-size:18px;margin:0 0 4px;color:#1a1a1a;">À faire en ${esc(monthLabel(list.month))}</h2>
    <p style="margin:0;color:#666;">${todoCount(list)} tâche(s) prête(s). Les cases se cochent dans le CRM, onglet Communications → À faire.</p>
    ${section("Leads à traiter (en attente de Sun Is Up)", list.leads, "Aucun lead en attente de Sun Is Up.",
      i => `<b>${esc(i.title)}</b> · ${esc(i.periodLabel)} · ${esc(i.subtitle)}`)}
    ${section("Onboarding des nouveaux membres", list.onboarding, "Aucun nouveau membre à accueillir.",
      i => `<b>${esc(i.title)}</b> · ${esc(i.subtitle)}`)}
    ${section("Invitations à la plateforme (comptes non activés)", list.invites.ready, "Tous les membres actifs ont un accès ou ont été invités récemment.",
      i => `<b>${esc(i.title)}</b> · ${esc(i.periodLabel)} · ${esc(i.subtitle)}`)}
    ${section("Rapports semestriels à envoyer", list.reports, "Aucun rapport à envoyer.",
      i => `<b>${esc(i.title)}</b> · ${esc(i.periodLabel)}${i.overdue ? " · <span style=\"color:#b45309;\">en retard</span>" : ""} · ${esc(i.subtitle)}`)}
    ${section("Prosumers : facture ou décompte selon le solde", list.prosumers.ready, "Aucun prosumer prêt.",
      i => `<b>${esc(i.title)}</b> · ${esc(i.periodLabel)}${i.deferrals ? ` · décalé ${i.deferrals} fois` : ""} · ${esc(i.subtitle)}`)}
    ${section("Remboursements des producteurs", list.reimbursements.ready, "Aucun remboursement prêt.",
      i => `<b>${esc(i.title)}</b> · ${esc(i.periodLabel)} · ${esc(i.subtitle)}`)}
    ${section("Facturation des consommateurs", list.invoices.ready, "Aucune facturation prête.",
      i => `<b>${esc(i.title)}</b> · ${esc(i.periodLabel)} · ${esc(i.subtitle)}${i.billingChannel ? ` · ${esc(BILLING_CHANNEL_LABELS[i.billingChannel])}` : ""}`)}
    ${list.invoices.ready.length > 0 ? `<p style="margin:6px 0 0;color:#666;">Par canal : ${BILLING_CHANNELS
      .map(ch => `${esc(BILLING_CHANNEL_LABELS[ch])} ${list.invoices.ready.filter(i => i.billingChannel === ch).length}`).join(" · ")}</p>` : ""}
    ${waiting ? `<h3 style="font-size:15px;margin:24px 0 8px;color:#1a1a1a;">En attente des données</h3><ul style="margin:0;padding-left:18px;color:#666;">${waiting}</ul>` : ""}
    <p style="margin:28px 0 0;"><a href="${esc(siteUrl)}" style="display:inline-block;background:#F8BD3D;color:#1a1a1a;padding:10px 18px;border-radius:8px;text-decoration:none;font-weight:600;">Ouvrir Sun Is Up</a></p>
    <hr style="border:none;border-top:1px solid #eee;margin:24px 0;">
    <p style="font-size:12px;color:#999;margin:0;">Récap automatique du CRM, envoyé le 1er de chaque mois.</p>
  </div>`;
}
